/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {AbstractSearchCriteriaService} from '../../shared/filter/abstract-search-criteria.service';
import {PartNumberChangeQueryModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-query.model';
import {SortDirection} from '../../api/generic/contracts/sort-direction.enum';
import {PartNumberChangesColumnEnum} from '../models/part-number-changes-column.enum';
import {PncSearchCriteriaModel} from '../models/pnc-search-criteria/pnc-search-criteria.model';
import {PncSearchCriteriaMapper} from '../models/pnc-search-criteria/pnc-search-criteria.mapper';
import {CodeFilterStateModel} from '../../shared/filter/code-filter/code-filter-state.model';
import {PncDoneFilterEnum} from '../models/pnc-done-filter.enum';
import {NullableBooleanCriterionContract} from '../../api/generic/contracts/nullable-boolean-criterion.contract';

@Injectable({
    providedIn: 'root'
})
export class PartNumberChangesSearchCriteriaService extends AbstractSearchCriteriaService<PartNumberChangesColumnEnum> {
    clear(): void {
        // clear stored filters
        this.stateMap.clear();

        // Sort criteria
        this.sortDirection = SortDirection.DESC;
        this.sortedBy = PartNumberChangesColumnEnum.CHANGED_ON;

        // Pagination
        this.pageSize = 20;
        this.pageNumber = 1;
        this.totalResults = 0;
    }

    public getPartNumberChangesQuery(hasPaginationCriteria?: boolean): PartNumberChangeQueryModel {
        const searchCriteria: PncSearchCriteriaModel = {
            typeCodes: this.mapCodeCriterion(PartNumberChangesColumnEnum.TYPE),
            number: this.mapStringCriterion(PartNumberChangesColumnEnum.NUMBER),
            creationDatetime: this.mapDateCriterion(PartNumberChangesColumnEnum.CREATION_DATETIME),
            partnerCodes: this.mapCodeCriterion(PartNumberChangesColumnEnum.PARTNER),
            updatedBy: this.mapStringCriterion(PartNumberChangesColumnEnum.UPDATED_BY),
            changedOn: this.mapDateCriterion(PartNumberChangesColumnEnum.CHANGED_ON),
            oldMaterialNumber: this.mapStringCriterion(PartNumberChangesColumnEnum.OLD_MATERIAL_NUMBER),
            statusCodes: this.mapCodeCriterion(PartNumberChangesColumnEnum.STATUS),
            newMaterialNumber: this.mapStringCriterion(PartNumberChangesColumnEnum.NEW_PART_NUMBER),
            technicianGroupCodes: this.mapCodeCriterion(PartNumberChangesColumnEnum.TECHNICIAN_GROUP),
            supplyingPackingPlantCodes: this.mapCodeCriterion(PartNumberChangesColumnEnum.SUPPLYING_PACKING_PLANT),
            createdBy: this.mapStringCriterion(PartNumberChangesColumnEnum.CREATED_BY),
            done: this.mapDoneCriterion()
        };

        return {
            searchCriteria: PncSearchCriteriaMapper.toPncSearchCriteriaContract(searchCriteria),
            paginationCriteria: hasPaginationCriteria ? this.paginationCriteria : this.resetPaginationCriteria
        };
    }

    mapDoneCriterion(): NullableBooleanCriterionContract {
        const filterValue = <CodeFilterStateModel> this.stateMap.get(PartNumberChangesColumnEnum.DONE);
        const doneChecked = filterValue?.filteredCodes?.find((entry) =>
                entry.code === PncDoneFilterEnum.DONE).checked;
        const undoneChecked = filterValue?.filteredCodes?.find((entry) =>
                entry.code === PncDoneFilterEnum.UNDONE).checked;
        const allChecked = filterValue?.all;

        let criterion: NullableBooleanCriterionContract;
        if (!allChecked && (doneChecked !== undoneChecked)) {
            criterion = {
                value: doneChecked
            };
        }
        return criterion;
    }
}
